/**
 * HeaderSamples - Display header validation with valid example and problematic headers
 *
 * Shows one valid header as an example (if any exist), followed by all headers
 * that have parsing issues (Unknown metadata fields).
 *
 * @param {Array} samples - Array of header sample objects (1 valid + all invalid)
 */
function HeaderSamples({ samples }) {
  if (!samples || samples.length === 0) return null

  // Separate valid and invalid headers
  const validSample = samples.find((sample) => sample.is_valid)
  const problematicSamples = samples.filter((sample) => !sample.is_valid)

  // If no problematic headers, don't show this section
  if (problematicSamples.length === 0) return null

  return (
    <div className="space-y-4">
      {/* Show valid example if exists */}
      {validSample && (
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Valid Header Example</h4>
          <div className="border rounded p-3 bg-green-50 border-green-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500">
                Lines {validSample.line_number}-{validSample.line_number + 1}
              </span>
              <span className="text-xs text-green-600 font-medium">✓ Valid Format</span>
            </div>
            <pre className="text-xs font-mono whitespace-pre-wrap bg-white p-2 rounded border border-green-300 overflow-x-auto">
              {validSample.line1}
              {'\n'}
              {validSample.line2}
            </pre>
          </div>
        </div>
      )}

      {/* Show problem headers */}
      <div>
        <h4 className="font-medium text-gray-900 mb-2">Problem Headers</h4>
        <p className="text-sm text-gray-600 mb-3">
          {problematicSamples.length} header{problematicSamples.length > 1 ? 's' : ''} with parsing
          issues:
        </p>
      </div>
      {problematicSamples.map((sample, idx) => {
        // Identify which fields are missing
        const missingFields = []
        if (sample.metadata_extracted.node_number === 'Unknown') missingFields.push('Node Number')
        if (sample.metadata_extracted.intersection_name === 'Unknown')
          missingFields.push('Intersection Name')
        if (sample.metadata_extracted.scenario === 'Unknown') missingFields.push('Scenario')
        if (sample.metadata_extracted.time_period === 'Unknown') missingFields.push('Time Period')

        return (
          <div key={idx} className="border rounded p-3 bg-red-50 border-red-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500">
                Lines {sample.line_number}-{sample.line_number + 1}
              </span>
              <span className="text-xs text-red-600 font-medium">
                ⚠ Missing: {missingFields.join(', ')}
              </span>
            </div>

            <div className="mb-2">
              <div className="text-xs font-semibold text-red-700 mb-1">Header Content:</div>
              <pre className="text-xs font-mono whitespace-pre-wrap bg-white p-2 rounded border border-red-300 overflow-x-auto">
                {sample.line1}
                {'\n'}
                {sample.line2}
              </pre>
            </div>

            {sample.metadata_extracted && (
              <div className="mt-2 pt-2 border-t border-red-200">
                <div className="text-xs font-semibold text-red-700 mb-1">What We Extracted:</div>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <MetadataField label="Node" value={sample.metadata_extracted.node_number} />
                  <MetadataField label="Scenario" value={sample.metadata_extracted.scenario} />
                  <MetadataField
                    label="Intersection"
                    value={sample.metadata_extracted.intersection_name}
                  />
                  <MetadataField label="Time" value={sample.metadata_extracted.time_period} />
                </div>
              </div>
            )}

            <div className="mt-2 pt-2 border-t border-red-200">
              <div className="text-xs">
                <span className="font-semibold text-red-700">Fix:</span>{' '}
                <span className="text-gray-700">
                  Ensure the header follows the format: Line 1 should have &quot;1: Intersection
                  Name&quot; and scenario/project, Line 2 should have section type and time period
                  (e.g., &quot;AM Peak&quot;).
                </span>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

/**
 * MetadataField - Display a single metadata field
 */
function MetadataField({ label, value }) {
  const isUnknown = value === 'Unknown'
  const valueClass = isUnknown ? 'text-yellow-600 font-medium' : 'text-gray-900'

  return (
    <div>
      <span className="text-gray-600">{label}:</span>{' '}
      <span className={valueClass}>{value || 'Unknown'}</span>
    </div>
  )
}

export default HeaderSamples
